/**
 * Aggregate Net Exposure books — Lab Aggregate dual-call via bet-service ForGroup.
 *
 * On load / filter change:
 *   1. `POST /v1/book/getBooksForBackendPreForGroup` — fast inventory
 *   2. `POST /v1/book/getBooksForBackendForGroup` — horse P/L + netExposure
 * Poll (~3s) refreshes only the full endpoint.
 *
 * Group math (one memberList party query) runs on Aggregate-API — UI is a thin
 * client. Demo / no bet URL → fixtures.
 */

import { useQuery } from '@tanstack/react-query'
import { type AggregateMeCaller } from '@/api/aggregateMe'
import { betFetch, hasLiveApi, hasLiveBet } from '@/api/client'
import {
  backendGroupsToEvents,
  mergeLabNetExposureEvents,
  preGroupsToEvents,
  type BookEventGroupBackend,
  type BookEventGroupPre,
} from '@/api/map/books'
import { keys } from '@/api/hooks/shared/keys'
import { ROLE_LABEL, type Role } from '@/data/types'
import {
  FIXTURE_EVENTS,
  type BookType,
  type NetExposureEvent,
  type SportName,
} from '@/features/net-exposure/fixture'

/** Lab polls Net Exposure every 3s (`reloadTime`). */
export const BOOKS_POLL_MS = 3_000

export type BooksQuery = {
  selectedType: BookType
  eventType: SportName
  eventname?: string
}

export type BooksForBackendRequest = {
  selectedType: BookType
  filter: {
    /** Placeholder — ForGroup resolves memberList server-side. */
    user?: string
    level?: string
    eventType: string
    eventname: string
    bookmakerSessionFlag?: string
    marketId?: string
  }
}

/** Lab hierarchy numbers on Aggregate memberList → ROLE_LABEL string. */
const LEVEL_NUM: Record<string, string> = {
  '1': ROLE_LABEL['company-master'],
  '2': ROLE_LABEL['triple-master'],
  '3': ROLE_LABEL['double-master'],
  '4': ROLE_LABEL['super-master'],
  '5': ROLE_LABEL.master,
  '6': ROLE_LABEL.player,
}

export function labLevelFromMemberList(level: string | undefined): string {
  const raw = (level ?? '').trim()
  if (!raw) return ROLE_LABEL['triple-master']
  if (LEVEL_NUM[raw]) return LEVEL_NUM[raw]!
  const compact = raw.replace(/\s+/g, '').toLowerCase()
  const byRole: Record<string, Role> = {
    companymaster: 'company-master',
    triplemaster: 'triple-master',
    doublemaster: 'double-master',
    supermaster: 'super-master',
    master: 'master',
    member: 'player',
    player: 'player',
  }
  const role = byRole[compact]
  if (role) return ROLE_LABEL[role]
  return raw
}

export type BookScopeRoot = { user: string; level: string }

/**
 * MemberList roots from `/v1/me` — used for workstation display / ladder
 * parent context. Live book totals come from ForGroup APIs (server-side).
 */
export function bookScopeRoots(me: AggregateMeCaller | null | undefined): BookScopeRoot[] {
  const list = me?.memberList ?? []
  return list
    .map((m) => {
      const user = String(m._id ?? '').trim()
      if (!user) return null
      return { user, level: labLevelFromMemberList(m.level) }
    })
    .filter((x): x is BookScopeRoot => Boolean(x))
}

function buildForGroupRequest(query: BooksQuery): BooksForBackendRequest {
  return {
    selectedType: query.selectedType,
    filter: {
      eventType: query.eventType === 'All' ? 'All' : query.eventType,
      eventname: query.eventname?.trim() || 'All',
      bookmakerSessionFlag: 'all',
    },
  }
}

function queryKeyFields(query: BooksQuery) {
  return { ...query, scope: 'for-group' as const }
}

async function loadBooksPre(query: BooksQuery): Promise<NetExposureEvent[]> {
  if (!hasLiveApi()) return FIXTURE_EVENTS
  if (!hasLiveBet()) return []

  const groups = await betFetch<BookEventGroupPre[]>(
    '/v1/book/getBooksForBackendPreForGroup',
    {
      method: 'POST',
      body: JSON.stringify(buildForGroupRequest(query)),
    },
  )
  return mergeLabNetExposureEvents(preGroupsToEvents(groups ?? [], query.eventType))
}

async function loadBooks(query: BooksQuery): Promise<NetExposureEvent[]> {
  if (!hasLiveApi()) return FIXTURE_EVENTS
  if (!hasLiveBet()) return []

  const groups = await betFetch<BookEventGroupBackend[]>(
    '/v1/book/getBooksForBackendForGroup',
    {
      method: 'POST',
      body: JSON.stringify(buildForGroupRequest(query)),
    },
  )
  return mergeLabNetExposureEvents(backendGroupsToEvents(groups ?? [], query.eventType))
}

/** Lab Aggregate Pre ForGroup — inventory only (NE cells 0). */
export function useBooksForBackendPre(query: BooksQuery) {
  const live = hasLiveApi()
  const rootsKey = live ? 'live' : 'demo'

  return useQuery({
    queryKey: keys.booksPre({ ...queryKeyFields(query), scope: rootsKey }),
    queryFn: () => loadBooksPre(query),
    enabled: true,
    staleTime: live ? BOOKS_POLL_MS : Infinity,
    placeholderData: (previous) => previous,
  })
}

/** Lab Aggregate full ForGroup — horse P/L + NE; polls while live. */
export function useBooksForBackend(query: BooksQuery) {
  const live = hasLiveApi() && hasLiveBet()
  const rootsKey = live ? 'live' : 'demo'

  return useQuery({
    queryKey: keys.books({ ...queryKeyFields(query), scope: rootsKey }),
    queryFn: () => loadBooks(query),
    enabled: true,
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab load path: fire Pre + full together; show Pre until full arrives, then
 * prefer full. Demo sessions stay on fixtures via either loader.
 */
export function useNetExposureBooks(query: BooksQuery) {
  const pre = useBooksForBackendPre(query)
  const full = useBooksForBackend(query)

  const events = full.data ?? pre.data ?? []
  const isLoading = !full.data && !pre.data && (full.isLoading || pre.isLoading)
  const isError = Boolean(full.isError && pre.isError)
  const error = full.error ?? pre.error
  const isFetching = full.isFetching || pre.isFetching

  return {
    events,
    isLoading,
    isError,
    error,
    isFetching,
    pre,
    full,
    refetch: () => {
      void pre.refetch()
      void full.refetch()
    },
  }
}
